import { useTranslation } from 'react-i18next';
import InfoTooltip from '~/components/data-display/InfoTooltip/InfoTooltip';
import SimpleTooltip from '~/components/data-display/SimpleTooltip';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import IconArrowDiagonal from '~/icons/icon-arrow-diagonal.svg?react';
import { useTransactionSuspenseQuery } from '~/services/transaction';
import { Reconciliation } from '~/types/reconciliation';
import { isDefined } from '~/types/typeGuards';
import { absMoney, formatMoney } from '~/utils/money';
import {
  useBookkeepingTransactionId,
  useTransactionIds,
} from '~/views/bookkeeping/RegistrationAside/SingleRegistrationAsideState';
import BookkeepingReconciliationDetailCard from '~/views/bookkeeping/reconciliations/BookkeepingReconciliationDetailCard';
import styles from './BookkeepingTransactionFormHeader.module.scss';

const ReconciliationsTooltip = ({ reconciliations }: { reconciliations?: Reconciliation[] }) => {
  const { t } = useTranslation('bookkeeping');

  if (!reconciliations || reconciliations.length === 0) {
    return null;
  }

  return (
    <HStack gap={1} align="center" className={styles.reconciliationsTooltip}>
      <Text variant="body1" weight="semibold">
        {t('transactions.reconciliations_count', { count: reconciliations.length })}{' '}
      </Text>
      <InfoTooltip
        title={t('transactions.reconciliations_count', { count: reconciliations.length })}
        titleProps={{
          variant: 'body1',
          weight: 'semibold',
        }}
      >
        <VStack gap={2}>
          {reconciliations.map((reconciliation) => (
            <BookkeepingReconciliationDetailCard reconciliation={reconciliation} key={reconciliation.id} />
          ))}
        </VStack>
      </InfoTooltip>
    </HStack>
  );
};

const BookkeepingTransactionFormHeader: React.FC = () => {
  const { t } = useTranslation('bookkeeping');
  const transactionIds = useTransactionIds();
  const bookkeepingTransactionId = useBookkeepingTransactionId();

  const isInternalTransfer = transactionIds.length === 2;

  const { data: transaction } = useTransactionSuspenseQuery(transactionIds[0]);

  return (
    <HStack className={styles.container} justify="space-between">
      <VStack gap={1}>
        {transaction ? (
          <>
            <Text variant="title4" weight="light">
              {t('transactions.update_title')}
            </Text>
            <Text variant="body1" weight="semibold">
              {transaction.counterpartName ?? transaction.description}
            </Text>
            {transaction.counterpartName && (
              <Text variant="body2" weight="regular">
                {transaction.description}
              </Text>
            )}
            <HStack gap={1}>
              <Text variant="body2" weight="semibold">
                {formatMoney(isDefined(bookkeepingTransactionId) ? absMoney(transaction.amount) : transaction.amount)}
              </Text>

              {isInternalTransfer && isDefined(bookkeepingTransactionId) && (
                <SimpleTooltip title={t('transactions.update.internal_transfer_tooltip')} theme="dark">
                  <IconArrowDiagonal className={styles.transfer} />
                </SimpleTooltip>
              )}
            </HStack>
          </>
        ) : (
          <Text variant="title4" weight="light">
            {t('transactions.create_title')}
          </Text>
        )}
      </VStack>

      {transaction && <ReconciliationsTooltip reconciliations={transaction.reconciliations} />}
    </HStack>
  );
};

export default BookkeepingTransactionFormHeader;
